import React, { useEffect, useState, useMemo } from "react";
import { Form, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { mapUserRoles } from "../../../functions/useRoleMapping";
import { useSelector, useDispatch } from "react-redux";
import useHandleBlur from "../../../functions/useHandleBlur";
import useFieldClass from "../../../functions/useFieldClass";
// import { useFetchJobDetails } from "../../../functions/useFetchJobDetails"; // Reusable API hook
import { useFormState } from "../../../functions/useFormState"; // Reusable form hook
import {
  selectUserDetails,
  selectUserRoles,
} from "../../../redux/slices/userSlice";
import { selectApiUrls } from "../../../redux/slices/apiUrlSlice";
import { selectSkyHarvester, selectFieldDescriptions } from "../../../redux/selectors";

const SkyHarvester = (zone) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(selectUserDetails); // User details
  const userRoles = useSelector(selectUserRoles); // User details
  const currentUser = userDetails?.name;
  const sectionName = "skyHarvester"; // Name of the component

  const apiUrls = useSelector(selectApiUrls); // Fetch API URLs from Redux
  const apiUrl = apiUrls[sectionName];
  const tableKey = sectionName;

  const { options } = useSelector((state) => state.dropdown); // Fetch dropdown values from redux

  /****************Add dropdown options here*************************/

  const genericOptions =
    options
      ?.filter((option) => option.DropdownType === "Generic Yes/ No")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  /******************************************************************/

  const storedJobDetails = useSelector(selectSkyHarvester);
  const fieldDescriptions = useSelector(selectFieldDescriptions);

  const activetab = useSelector(
    (state) => state.activeMechanicalTab.selectedZone
  );

  const loading = storedJobDetails.length === 0;
  const error = null;

  const jobDetails = useMemo(() => {
    const detail = storedJobDetails.find((detail) => detail.Zone === activetab);
    return detail || storedJobDetails[0] || {};
  }, [storedJobDetails, activetab]);

  const { formData, handleChange, setFormData } = useFormState({});

  useEffect(() => {
    if (jobDetails) {
      setFormData({
        ID: jobDetails.ID || "",
        tankSize: jobDetails.TankSize || "",
        tankQuantity: jobDetails.TankQuantity || "",
        watertronicsProvidedTank: jobDetails.WatertronicsProvidedTank || "",
        providedTankDetails: jobDetails.ProvidedTankDetails || "",
        comments: jobDetails.Comments || "",
      });
    }
  }, [dispatch, jobDetails, setFormData]);

  const jobCheckoutStatus = useSelector((state) => state.checkout.job);
  const isJobCheckedOut = jobCheckoutStatus?.IsCheckedOut === true;
  const isCurrentUserCheckedOut =
    jobCheckoutStatus?.CheckedOutBy === currentUser;
  const readOnly = useSelector((state) => state.readOnly.isReadOnly); // Fetch read-only state from Redux
  const isDisabled = (isJobCheckedOut && !isCurrentUserCheckedOut) || readOnly; // Update isDisabled condition

  // State to store role variables
  const [roles, setRoles] = useState({
    Admin: false,
    Apps: false,
    Engineer: false,
    TestPit: false,
    PSN: false,
    Sales: false,
    ReadOnly: false,
  });

  const { handleFocus, handleBlur } = useHandleBlur(formData);

  useEffect(() => {
    setRoles(mapUserRoles(userRoles));
  }, [userRoles]);

  const fieldClasses = useFieldClass(formData, zone); // Use useFieldClass hook

  const isValueInDropdown = (value, dropdownOptions) => {
    const exists = dropdownOptions.some(
      (option) => option.DisplayText === value || option.displayName === value
    );
    return exists;
  };

  const getFieldTitle = (fieldName) => {
    return (
      fieldDescriptions.find((desc) => desc.ColumnName === fieldName)
        ?.Description || ""
    );
  };

  const renderTooltip = (fieldName) => (
    <Tooltip id={`tooltip-${fieldName}`}>
      {getFieldTitle(fieldName)
        .split("\n")
        .map((line, index) => (
          <span key={index}>
            {line}
            <br />
          </span>
        ))}
    </Tooltip>
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    ); // Show loading spinner
  }

  if (error) {
    return <div className="error">{error}</div>; // Show error message if fetch fails
  }

  return (
    <div className="mechanical-responsive-grid sky-harvester-responsive">
      <Row className="mb-3">
        <Col>
          <Form.Group as={Row} controlId="tankSize">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("TankSize")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Tank Size</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Control
                type="text"
                name="tankSize"
                value={formData.tankSize || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Tank Size", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "TankSize",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.tankSize}
                onChange={handleChange}
                placeholder="Enter Tank Size"
              />
            </Col>
          </Form.Group>
        </Col>

        <Col>
          <Form.Group as={Row} controlId="tankQuantity">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("TankQuantity")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Tank Quantity</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Control
                type="text"
                name="tankQuantity"
                value={formData.tankQuantity || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Tank Quantity", [], {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "TankQuantity",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.tankQuantity}
                onChange={handleChange}
                placeholder="Enter Tank Quantity"
              />
            </Col>
          </Form.Group>
        </Col>

        <Col className="mechanical-form-fill-row">
          <Form.Group as={Row} controlId="watertronicsProvidedTank">
            <Form.Label column md={3}>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("WatertronicsProvidedTank")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Watertronics Provided Tank</span>
              </OverlayTrigger>
            </Form.Label>
            <Col>
              <Form.Select
                name="watertronicsProvidedTank"
                value={formData.watertronicsProvidedTank || ""}
                onFocus={handleFocus}
                onBlur={(e) =>
                  handleBlur(e, "Watertronics Provided Tank", genericOptions, {
                    ID: formData.ID,
                    url: apiUrl,
                    field: "WatertronicsProvidedTank",
                    tableKey,
                  })
                }
                disabled={isDisabled || (!roles.Admin && !roles.Apps)}
                className={fieldClasses.watertronicsProvidedTank}
                onChange={handleChange}
              >
                <option value="">Select Watertronics Provided Tank</option>
                {genericOptions.map((option) => (
                  <option key={option.ID} value={option.DisplayText}>
                    {option.DisplayText}
                  </option>
                ))}
                {!isValueInDropdown(
                  formData.watertronicsProvidedTank,
                  genericOptions
                ) &&
                  formData.watertronicsProvidedTank && (
                    <option
                      value={formData.watertronicsProvidedTank}
                      style={{ color: "red" }}
                    >
                      *{formData.watertronicsProvidedTank}
                    </option>
                  )}
              </Form.Select>
            </Col>
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3 mechanical-form-full-width">
        <Col>
          <Form.Group controlId="providedTankDetails">
            <Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("ProvidedTankDetails")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Provided Tank Details</span>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              name="providedTankDetails"
              value={formData.providedTankDetails || ""}
              onFocus={handleFocus}
              onBlur={(e) =>
                handleBlur(e, "Provided Tank Details", [], {
                  ID: formData.ID,
                  url: apiUrl,
                  field: "ProvidedTankDetails",
                  tableKey,
                })
              }
              disabled={isDisabled || (!roles.Admin && !roles.Apps)}
              className={fieldClasses.providedTankDetails}
              onChange={handleChange}
              placeholder="Enter Provided Tank Details"
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="mb-3 mechanical-form-full-width">
        <Col>
          <Form.Group controlId="comments">
            <Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip("Comments")}
                delay={{ show: 300, hide: 200 }}
              >
                <span>Comments</span>
              </OverlayTrigger>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="comments"
              value={formData.comments || ""}
              onFocus={handleFocus}
              onBlur={(e) =>
                handleBlur(e, "Comments", [], {
                  ID: formData.ID,
                  url: apiUrl,
                  field: "Comments",
                  tableKey,
                })
              }
              disabled={isDisabled || (!roles.Admin && !roles.Apps)}
              className={fieldClasses.comments}
              onChange={handleChange}
              placeholder="Enter Comments"
            />
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default SkyHarvester;
